import { useState } from 'react';

import { useQuery } from '@apollo/client';

import { ContentTypeEnum } from 'constants-content';
import { InvoiceType } from 'graphql-query-contracts';
import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import { WorkOrderModuleSettings } from 'lane-shared/domains/workOrder';
import { channelModuleByChannelId } from 'lane-shared/graphql/modules';
import { useWorkOrdersServiceRequestInvoicingOptionsEnabled } from 'react-hooks';

import { toGraphQLContentType } from 'helpers/contentTypeMapper';

import {
  getInvoicingOptionsEnabledValue,
  showChargeStatus,
} from '../../helpers/getInvoicingOptionsEnabledValue';
import { initialInvoiceType } from './initialInvoiceType';

export const DEFAULT_INVOICE_DUE_IN_DAYS = 30;

export function useInvoiceOptionsForChannel(channelId: string) {
  const { currency, invoiceDueInDays, isInvoicingDisabled, isTaxPolicyLoaded } =
    useBillingPaymentSettingsForChannel({ channelId });
  const { data, loading: isModuleLoading } = useQuery(
    channelModuleByChannelId,
    {
      variables: {
        channelId,
        contentType: toGraphQLContentType(ContentTypeEnum.WorkOrder),
      },
    }
  );
  const settings: WorkOrderModuleSettings | undefined =
    data?.channelModuleByChannelId?.settings;
  const isInvoicingOptionsFlagEnabled =
    useWorkOrdersServiceRequestInvoicingOptionsEnabled();

  const [selectedInvoiceType, setSelectedInvoiceType] = useState<InvoiceType>();
  const [invoiceDueDate, setInvoiceDueDate] = useState<Date>();
  const invoiceType =
    selectedInvoiceType ??
    initialInvoiceType({
      isInvoicingOptionsFlagEnabled,
      isInvoicingDisabled,
      isInvoiceOptionsEnabled: settings?.isInvoiceOptionsEnabled,
    });

  const updateChargeDetails = (
    value: InvoiceType | Date,
    fieldname: string
  ) => {
    if (fieldname === 'invoiceType' && typeof value === 'string') {
      setSelectedInvoiceType(value);
    }

    if (fieldname === 'invoiceDueDate' && value instanceof Date) {
      setInvoiceDueDate(value);
    }
  };

  return {
    loading: isModuleLoading || !isTaxPolicyLoaded,
    currency,
    invoiceDueInDays: invoiceDueInDays ?? DEFAULT_INVOICE_DUE_IN_DAYS,
    invoiceType,
    invoiceDueDate,
    updateChargeDetails,
    isInvoiceOptionEnabled:
      isInvoicingOptionsFlagEnabled &&
      getInvoicingOptionsEnabledValue(
        isInvoicingDisabled,
        settings?.isInvoiceOptionsEnabled
      ),
    showSRChargeStatus:
      !isInvoicingOptionsFlagEnabled ||
      showChargeStatus(isInvoicingDisabled, settings?.isInvoiceOptionsEnabled),
  };
}
